/**
 * Live per-tenant agent on/off switches.
 * Lists every catalog agent for the selected business (grouped by tenant in
 * the All Businesses view) with an inline toggle backed by /api/agents/config.
 */
import React, { useEffect, useState } from 'react';
import { rcmApi, type AgentConfig } from '../api/rcmApi';
import { usePortCo } from '../context/PortCoContext';

export const AgentSwitchPanel: React.FC = () => {
  const { selectedPortco } = usePortCo();
  const [configs, setConfigs] = useState<AgentConfig[]>([]);
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await rcmApi.getAgentConfigs(selectedPortco);
        if (cancelled) return;
        const map: Record<string, boolean> = {};
        for (const c of list) map[`${c.portcoId}|${c.workstream}`] = c.enabled;
        setConfigs(list);
        setEnabled(map);
        setError(null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load agent switches');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPortco]);

  const toggle = async (c: AgentConfig, next: boolean) => {
    const key = `${c.portcoId}|${c.workstream}`;
    setEnabled((prev) => ({ ...prev, [key]: next })); // optimistic
    try {
      await rcmApi.setAgentConfig(c.portcoId, c.workstream, next);
    } catch (e: any) {
      setEnabled((prev) => ({ ...prev, [key]: !next })); // rollback
      setError(e?.message || 'Failed to update agent');
    }
  };

  if (error && !loaded) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <p className="text-xs text-slate-500">{error}</p>
      </div>
    );
  }

  const grouped = configs.reduce<Record<string, AgentConfig[]>>((acc, c) => {
    (acc[c.portcoId] ||= []).push(c);
    return acc;
  }, {});

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Agent Switches</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Turn any agent off for a business — disabled agents stop running and their actions are blocked
          </p>
        </div>
        <span className="text-[10px] font-semibold text-slate-400">
          {loaded ? `${configs.filter((c) => enabled[`${c.portcoId}|${c.workstream}`] ?? true).length}/${configs.length} on` : '…'}
        </span>
      </div>
      {!loaded ? (
        <p className="text-xs text-slate-400 py-4 text-center">Loading agent switches…</p>
      ) : (
        Object.entries(grouped).map(([tenant, list]) => (
          <div key={tenant} className={selectedPortco === 'all' ? 'mb-2 last:mb-0' : ''}>
            {selectedPortco === 'all' && (
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                {tenant.replace('portco_', '').replace(/_/g, ' ')}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
              {list.map((c) => {
                const on = enabled[`${c.portcoId}|${c.workstream}`] ?? true;
                return (
                  <div
                    key={`${c.portcoId}|${c.workstream}`}
                    className={`flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 border ${
                      on ? 'bg-slate-50 border-slate-200' : 'bg-rose-50/60 border-rose-100'
                    }`}
                  >
                    <span className="text-[11px] font-medium text-slate-700 leading-tight truncate" title={c.name}>
                      {c.name}
                    </span>
                    <button
                      role="switch"
                      aria-checked={on}
                      disabled={!loaded}
                      onClick={() => toggle(c, !on)}
                      title={on ? 'Disable this agent' : 'Enable this agent'}
                      className={`relative inline-flex h-4.5 w-8 items-center rounded-full transition-colors shrink-0 cursor-pointer disabled:opacity-40 h-5 w-9 ${
                        on ? 'bg-mantis-500' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                          on ? 'translate-x-4.5 translate-x-[18px]' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
      {error && loaded && <p className="text-[11px] text-red-500 mt-2">{error}</p>}
    </div>
  );
};
